import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { ModelSelector, type ModelSelectorValue } from "./ModelSelector";

/** JSON de model_fallback como a API guarda (agente, follow-up e tool). */
export interface ModelFallbackJson {
  model_provider?: string | null;
  model_name?: string;
  temperature?: number;
  reasoning_effort?: string | null;
  verbosity?: string | null;
  max_tokens?: number;
}

interface CatalogModel {
  provider: string;
  model_name: string;
  thinking_levels?: string[];
}

/** Primeiro modelo do catálogo: "Adicionar" já nasce com modelo válido. Sem
 *  isso o select mostra o primeiro item mas nada fica selecionado, e o save
 *  grava null — o usuário acha que cadastrou fallback e não cadastrou. */
function firstFromCatalog(catalog: CatalogModel[]): ModelSelectorValue | null {
  const m = catalog[0];
  if (!m) return null;
  const levels = m.thinking_levels ?? [];
  return {
    provider: m.provider,
    model_name: m.model_name,
    temperature: 0.7,
    reasoning_effort: levels.length ? (levels.includes("off") ? "off" : levels[0]) : "off",
    verbosity: "default",
    maxTokens: 4096,
  };
}

export function fallbackToForm(fb: ModelFallbackJson | null | undefined): ModelSelectorValue | null {
  if (!fb?.model_provider || !fb?.model_name) return null;
  return {
    provider: fb.model_provider,
    model_name: fb.model_name,
    temperature: fb.temperature ?? 0.7,
    reasoning_effort: fb.reasoning_effort ?? "off",
    verbosity: fb.verbosity ?? "default",
    maxTokens: fb.max_tokens ?? 4096,
  };
}

/** null quando não há modelo escolhido — a API entende como "sem fallback". */
export function formToFallback(v: ModelSelectorValue | null): ModelFallbackJson | null {
  if (!v?.model_name) return null;
  return {
    model_provider: v.provider,
    model_name: v.model_name,
    temperature: v.temperature,
    reasoning_effort: v.reasoning_effort === "off" ? null : v.reasoning_effort,
    verbosity: !v.verbosity || v.verbosity === "default" ? null : v.verbosity,
    max_tokens: v.maxTokens ?? 4096,
  };
}

const titleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: "var(--ink)",
  margin: 0,
};

const hintStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--ink-secondary)",
  margin: "4px 0 0",
};

const linkBtn: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "var(--brand)",
  fontSize: 12,
  cursor: "pointer",
  padding: 0,
};

/**
 * Bloco "Modelo de fallback": o segundo modelo, usado quando o principal erra.
 * Vazio = sem fallback (o erro do principal continua sendo erro).
 */
export function ModelFallbackSection({
  value,
  onChange,
  showVerbosity = false,
  hint = "Usado se o modelo principal falhar. A execução fica como sucesso parcial.",
}: {
  value: ModelSelectorValue | null;
  onChange: (next: ModelSelectorValue | null) => void;
  showVerbosity?: boolean;
  hint?: string;
}) {
  // Mesma queryKey do ModelSelector: sai do cache, sem request extra.
  const { data: catalog = [] } = useQuery<CatalogModel[]>({
    queryKey: ["available-models"],
    queryFn: () => api.get("/dev/available-models"),
  });
  const seed = firstFromCatalog(catalog);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ borderTop: "1px solid var(--line)", paddingTop: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <h4 style={titleStyle}>Modelo de fallback</h4>
          {value ? (
            <button type="button" style={linkBtn} onClick={() => onChange(null)}>
              Remover
            </button>
          ) : (
            <button
              type="button"
              style={{ ...linkBtn, opacity: seed ? 1 : 0.5, cursor: seed ? "pointer" : "not-allowed" }}
              disabled={!seed}
              onClick={() => seed && onChange(seed)}
            >
              Adicionar
            </button>
          )}
        </div>
        <p style={hintStyle}>
          {catalog.length === 0
            ? "Nenhum modelo cadastrado (Painel Dev → Modelos)."
            : hint}
        </p>
      </div>
      {value && (
        <ModelSelector
          showMaxTokens
          showVerbosity={showVerbosity}
          value={value}
          onChange={onChange}
        />
      )}
    </div>
  );
}
